// Role Permissions Configuration
// This file defines all permissions for each role in the application

export const ROLES = {
  SUPER_ADMIN: 'superadmin',
  ORG_ADMIN: 'org_admin', // NEW: Organization Admin - can manage organizations and their hierarchy
  ADMIN: 'admin',
  USER: 'user', // Company Admin
  ACCOUNTING: 'accounting',
  COMPANY_STAFF: 'company_staff',
  MANUFACTURER: 'manufacturer', // Manufacturing Partner Role
  MANUFACTURER_STAFF: 'manufacturer_staff', // A manufacturer's staff: manufacturer access minus user management
  CUSTOMER: 'customer', // End-user customer with limited admin access
  // Multi-Leg Shipment Orchestration operational roles (spec §14)
  OPERATIONS: 'operations',
  DISPATCH: 'dispatch',
  BROKERAGE: 'brokerage',
  WAREHOUSE: 'warehouse'
};

// Non-admin roles that may invite teammates, mapped to the roles they may assign.
// A role is listed only if its effective permissions are a subset of the inviter's;
// company_staff and customer both carry permissions a manufacturer lacks (billing,
// carriers, rate pricing, ...), so a manufacturer may only add manufacturers. The
// adminInviteUser callable enforces the same map (functions/src/admin-user-management.js).
export const TEAM_INVITE_ROLES = {
  [ROLES.MANUFACTURER]: [ROLES.MANUFACTURER, ROLES.MANUFACTURER_STAFF],
  // An org admin manages the users of its own companies (company-scoped, not
  // customer-scoped: TEAM_COMPANY_SCOPED_ROLES) at its role or below.
  [ROLES.ORG_ADMIN]: [ROLES.ORG_ADMIN, ROLES.USER, ROLES.COMPANY_STAFF, ROLES.MANUFACTURER, ROLES.MANUFACTURER_STAFF, ROLES.CUSTOMER],
};

// Team managers whose team is the users of their companies rather than of their
// customers. Mirrors functions/src/teamAccess.js.
export const TEAM_COMPANY_SCOPED_ROLES = [ROLES.ORG_ADMIN];

// Roles that act as a company's administrator in the app: the Company Admin and the org
// admin (a Company Admin across its org's companies, with cost and user management on
// top). Use instead of `role === 'user'` wherever a screen special-cases company admins.
export const COMPANY_ADMIN_ROLES = [ROLES.USER, ROLES.ORG_ADMIN];
export const isCompanyAdminRole = (role) => COMPANY_ADMIN_ROLES.includes(role);

// Roles that act for a manufacturer. Use wherever code special-cases the manufacturer
// role (cost hiding, single-company scoping) so staff are treated exactly the same.
export const MANUFACTURER_ROLES = [ROLES.MANUFACTURER, ROLES.MANUFACTURER_STAFF];
export const isManufacturerRole = (role) => MANUFACTURER_ROLES.includes(role);

// Convenience: ops roles that can see all routing legs.
export const OPS_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATIONS, ROLES.DISPATCH];

// Roles allowed to see customs legs (in addition to ops roles).
export const BROKERAGE_VISIBLE_ROLES = [...OPS_ROLES, ROLES.BROKERAGE];

// Roles allowed to see warehouse legs (in addition to ops roles).
export const WAREHOUSE_VISIBLE_ROLES = [...OPS_ROLES, ROLES.WAREHOUSE];

/**
 * Multi-leg visibility helper — returns true if the given role can see a leg
 * of the given type. Customers never see internal legs.
 */
export const canViewLeg = (role, legType, visibility = 'internal') => {
  if (visibility === 'external') return true;
  if (OPS_ROLES.includes(role)) return true;
  if (legType === 'customs' && BROKERAGE_VISIBLE_ROLES.includes(role)) return true;
  if (legType === 'warehouse' && WAREHOUSE_VISIBLE_ROLES.includes(role)) return true;
  return false;
};

// Define all available permissions in the system
export const PERMISSIONS = {
  // Dashboard & General Access
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ADMIN_DASHBOARD: 'view_admin_dashboard',
  
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  MANAGE_USER_ROLES: 'manage_user_roles',
  INVITE_USERS: 'invite_users',
  RESET_USER_PASSWORD: 'reset_user_password',
  
  // Company Management
  VIEW_COMPANIES: 'view_companies',
  CREATE_COMPANIES: 'create_companies',
  EDIT_COMPANIES: 'edit_companies',
  DELETE_COMPANIES: 'delete_companies',
  VIEW_ALL_COMPANIES: 'view_all_companies', // Super admin can see all companies
  
  // Organization Management
  VIEW_ORGANIZATIONS: 'view_organizations',
  CREATE_ORGANIZATIONS: 'create_organizations',
  EDIT_ORGANIZATIONS: 'edit_organizations',
  DELETE_ORGANIZATIONS: 'delete_organizations',
  
  // Shipment Management
  VIEW_SHIPMENTS: 'view_shipments',
  CREATE_SHIPMENTS: 'create_shipments',
  USE_QUICKSHIP: 'use_quickship', // Specific permission for QuickShip functionality
  EDIT_SHIPMENTS: 'edit_shipments',
  DELETE_SHIPMENTS: 'delete_shipments',
  VIEW_ALL_SHIPMENTS: 'view_all_shipments', // Admin can see all shipments
  EXPORT_SHIPMENTS: 'export_shipments',
  MANAGE_DRAFT_SHIPMENTS: 'manage_draft_shipments',
  REVIEW_SHIPMENTS: 'review_shipments', // NEW: Permission to review and approve shipments
  // The booking-reconcile queue (/admin/booking-reconcile): record what the carrier says
  // about a booking whose outcome is unknown. The callable allows superadmin, admin,
  // operations and dispatch (functions/src/booking/bookingReconcile.js RECONCILE_ROLES).
  RESOLVE_BOOKING_RECONCILE: 'resolve_booking_reconcile',
  
  // Shipment Document & Action Permissions
  VIEW_DOCUMENTS: 'view_documents', // NEW: View documents section (BOL, carrier confirmation, uploads)
  VIEW_BOL: 'view_bol', // NEW: View and print BOL documents
  VIEW_CARRIER_CONFIRMATION: 'view_carrier_confirmation', // NEW: View and print carrier confirmations
  VIEW_FOLLOW_UPS: 'view_follow_ups', // NEW: View and manage follow-up tasks
  ARCHIVE_SHIPMENT: 'archive_shipment', // NEW: Archive shipments
  CANCEL_SHIPMENT: 'cancel_shipment', // NEW: Cancel shipments
  
  // Financial Information
  VIEW_SHIPMENT_COSTS: 'view_shipment_costs', // NEW: View cost and profit information
  VIEW_SHIPMENT_FINANCIALS: 'view_shipment_financials', // NEW: View detailed financial breakdown
  
  // Legacy Carrier Confirmations (keeping for backward compatibility)
  GENERATE_CARRIER_CONFIRMATIONS: 'generate_carrier_confirmations', // NEW: Generate/regenerate carrier confirmations
  
  // Customer Management
  VIEW_CUSTOMERS: 'view_customers',
  CREATE_CUSTOMERS: 'create_customers',
  EDIT_CUSTOMERS: 'edit_customers',
  DELETE_CUSTOMERS: 'delete_customers',
  VIEW_ALL_CUSTOMERS: 'view_all_customers',
  SET_DEFAULT_ADDRESSES: 'set_default_addresses', // NEW: Ability to set default shipfrom/shipto addresses
  
  // Address Book Management
  VIEW_ADDRESSES: 'view_addresses',
  CREATE_ADDRESSES: 'create_addresses',
  EDIT_ADDRESSES: 'edit_addresses',
  DELETE_ADDRESSES: 'delete_addresses',
  VIEW_ALL_ADDRESSES: 'view_all_addresses',
  EXPORT_ADDRESSES: 'export_addresses',
  
  // Billing & Invoicing
  VIEW_BILLING: 'view_billing',
  CREATE_INVOICES: 'create_invoices',
  EDIT_INVOICES: 'edit_invoices',
  DELETE_INVOICES: 'delete_invoices',
  VIEW_ALL_INVOICES: 'view_all_invoices',
  MANAGE_PAYMENT_TERMS: 'manage_payment_terms',
  GENERATE_INVOICES: 'generate_invoices',
  VIEW_PAYMENTS: 'view_payments',
  MANAGE_AP_PROCESSING: 'manage_ap_processing',
  
  // Sales Commission Management
  VIEW_COMMISSIONS: 'view_commissions',
  MANAGE_SALES_PERSONS: 'manage_sales_persons',
  MANAGE_SALES_TEAMS: 'manage_sales_teams',
  CALCULATE_COMMISSIONS: 'calculate_commissions',
  GENERATE_COMMISSION_REPORTS: 'generate_commission_reports',
  SCHEDULE_COMMISSION_REPORTS: 'schedule_commission_reports',
  
  // Carrier Management
  VIEW_CARRIERS: 'view_carriers',
  CREATE_CARRIERS: 'create_carriers',
  EDIT_CARRIERS: 'edit_carriers',
  DELETE_CARRIERS: 'delete_carriers',
  MANAGE_CARRIER_KEYS: 'manage_carrier_keys',
  MANAGE_EDI_MAPPING: 'manage_edi_mapping',
  MANAGE_CARRIERS: 'manage_carriers', // Combined carrier management permission
  
  // Address Management (Combined permission for address book access)
  MANAGE_ADDRESSES: 'manage_addresses',
  
  // Broker Management
  MANAGE_BROKERS: 'manage_brokers',
  
  // Reports & Analytics
  VIEW_REPORTS: 'view_reports',
  CREATE_REPORTS: 'create_reports',
  SCHEDULE_REPORTS: 'schedule_reports',
  VIEW_ALL_REPORTS: 'view_all_reports',
  EXPORT_REPORTS: 'export_reports',
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_FOLLOWUPS: 'view_followups',
  
  // Tracking
  VIEW_TRACKING: 'view_tracking',
  UPDATE_TRACKING: 'update_tracking',
  MANUAL_STATUS_OVERRIDE: 'manual_status_override', // NEW: Ability to manually override shipment status
  
  // Profile Management
  VIEW_PROFILE: 'view_profile',
  EDIT_PROFILE: 'edit_profile',
  
  // Notification Management
  VIEW_NOTIFICATIONS: 'view_notifications',
  MANAGE_NOTIFICATIONS: 'manage_notifications',
  
  // System Settings
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings', // Platform System Settings + System Configuration pages (admin/superadmin only)
  MANAGE_ROLES: 'manage_roles',
  MANAGE_MARKUPS: 'manage_markups',
  
  // Quote Management
  CREATE_QUOTES: 'create_quotes',
  VIEW_QUOTES: 'view_quotes',

  // Advanced Features
  USE_LIVE_RATES: 'use_live_rates', // NEW: Permission for CreateShipmentX functionality
  USE_AI_AGENT: 'use_ai_agent',
  USE_ADVANCED_ROUTING: 'use_advanced_routing',
  MANAGE_INTEGRATIONS: 'manage_integrations',
  VIEW_DEVELOPER_PORTAL_V2: 'view_developer_portal_v2', // Soluship 2.0 bridge portal — superadmin only (granted to no role's hardcoded map)
  VIEW_ATLAS_AGENT: 'view_atlas_agent', // Atlas 2.0 voice AI agent — superadmin only for now (granted to no role's hardcoded map; grantable via the roles dialog)
  
  // Rate and Pricing Visibility
  VIEW_RATE_PRICING: 'view_rate_pricing',
  VIEW_RATE_BREAKDOWN: 'view_rate_breakdown',
  // Rate selection UI controls
  SHOW_RATE_FILTERS: 'show_rate_filters',
  
  // Shipment Information Fields
  VIEW_BILL_TYPE: 'view_bill_type',
  VIEW_ETA_FIELDS: 'view_eta_fields', // Legacy - keeping for backward compatibility
  EDIT_ETAS: 'edit_etas', // NEW: Consolidated ETA field editing
  EDIT_SHIPMENT_REFERENCES: 'edit_shipment_references', // NEW: Primary reference numbers
  EDIT_CARRIER_TRACKING: 'edit_carrier_tracking', // NEW: Carrier tracking number
  
  // QuickShip Form Controls
  SELECT_QUICKSHIP_CARRIER: 'select_quickship_carrier', // NEW: Select carrier in QuickShip
  SELECT_SHIP_FROM: 'select_ship_from', // NEW: Ship from address selection
  SELECT_SHIP_TO: 'select_ship_to', // NEW: Ship to address selection
  
  // Address Dropdown Visibility Controls
  VIEW_SHIPFROM_ADDRESSES: 'view_shipfrom_addresses', // NEW: View ShipFrom address dropdown in shipment forms
  VIEW_SHIPTO_ADDRESSES: 'view_shipto_addresses', // NEW: View ShipTo address dropdown in shipment forms
  
  // QuickShip Action Controls
  USE_SWITCH_TO_LIVE_RATES: 'use_switch_to_live_rates', // NEW: Switch to live rates button
  USE_SHIP_LATER: 'use_ship_later', // NEW: Ship later / save draft functionality
  USE_BOOK_SHIPMENT: 'use_book_shipment', // NEW: Book shipment functionality
  
  // Package Information Fields  
  VIEW_DECLARED_VALUE: 'view_declared_value',
  VIEW_FREIGHT_CLASS: 'view_freight_class',
  
  // ============================================
  // HIERARCHY PERMISSIONS (Multi-Level Organization Support)
  // ============================================
  
  // Hierarchy Viewing
  VIEW_HIERARCHY: 'view_hierarchy',                       // View organizational hierarchy tree
  VIEW_HIERARCHY_TREE: 'view_hierarchy_tree',             // View visual hierarchy tree component
  VIEW_DESCENDANT_DATA: 'view_descendant_data',           // View data from child companies/divisions
  
  // Division/Sub-Company Management
  CREATE_DIVISIONS: 'create_divisions',                   // Create child companies/divisions
  EDIT_DIVISIONS: 'edit_divisions',                       // Edit division settings
  DELETE_DIVISIONS: 'delete_divisions',                   // Delete divisions (soft delete)
  MOVE_DIVISIONS: 'move_divisions',                       // Move divisions to different parent
  MANAGE_CHILD_COMPANIES: 'manage_child_companies',       // Full management of child companies
  
  // Hierarchy Configuration
  CONFIGURE_BILLING_MODE: 'configure_billing_mode',       // Configure rollup vs independent billing
  CONFIGURE_INHERITANCE: 'configure_inheritance',         // Configure carrier/settings inheritance
  MANAGE_HIERARCHY_ACCESS: 'manage_hierarchy_access',     // Assign hierarchy access to users
  
  // Hierarchy Reporting
  VIEW_HIERARCHY_REPORTS: 'view_hierarchy_reports',       // View aggregated reports across hierarchy
  EXPORT_HIERARCHY_DATA: 'export_hierarchy_data',         // Export data across hierarchy branches
};

// Define role-based permissions
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: {
    // Super admin has ALL permissions - no limitations
    '*': true, // Special flag indicating all permissions
    
    // EXPLICIT PERMISSIONS FOR CRITICAL ROUTES (FALLBACK)
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true,
    [PERMISSIONS.MANAGE_ROLES]: true,
    [PERMISSIONS.VIEW_USERS]: true,
    [PERMISSIONS.CREATE_USERS]: true,
    [PERMISSIONS.EDIT_USERS]: true,
    [PERMISSIONS.DELETE_USERS]: true,
    [PERMISSIONS.VIEW_COMPANIES]: true,
    [PERMISSIONS.CREATE_COMPANIES]: true,
    [PERMISSIONS.EDIT_COMPANIES]: true,
    [PERMISSIONS.DELETE_COMPANIES]: true,
    
    // HIERARCHY PERMISSIONS (Super admin has full access)
    [PERMISSIONS.VIEW_HIERARCHY]: true,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: true,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: true,
    [PERMISSIONS.CREATE_DIVISIONS]: true,
    [PERMISSIONS.EDIT_DIVISIONS]: true,
    [PERMISSIONS.DELETE_DIVISIONS]: true,
    [PERMISSIONS.MOVE_DIVISIONS]: true,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: true,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: true,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: true,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: true,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: true,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: true,
  },

  // ORG_ADMIN: built below the matrix from the Company Admin row (ORG_ADMIN_PERMISSIONS).

  // ADMIN ROLE - Administrative access to manage the system
  [ROLES.ADMIN]: {
    // Dashboard & General Access
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true,
    
    // User Management
    [PERMISSIONS.VIEW_USERS]: true,
    [PERMISSIONS.CREATE_USERS]: true,
    [PERMISSIONS.EDIT_USERS]: true,
    [PERMISSIONS.DELETE_USERS]: true,
    [PERMISSIONS.MANAGE_USER_ROLES]: true,
    [PERMISSIONS.INVITE_USERS]: true,
    [PERMISSIONS.RESET_USER_PASSWORD]: true,
    
    // Company Management
    [PERMISSIONS.VIEW_COMPANIES]: true,
    [PERMISSIONS.CREATE_COMPANIES]: true,
    [PERMISSIONS.EDIT_COMPANIES]: true,
    [PERMISSIONS.DELETE_COMPANIES]: false, // Only super admin can delete
    [PERMISSIONS.VIEW_ALL_COMPANIES]: true,

    // Organization Management - Regular admins do NOT have org access
    // Only superadmins and explicitly assigned org admins can manage organizations
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,

    // Customer Management
    [PERMISSIONS.VIEW_CUSTOMERS]: true,
    [PERMISSIONS.CREATE_CUSTOMERS]: true,
    [PERMISSIONS.EDIT_CUSTOMERS]: true,
    [PERMISSIONS.DELETE_CUSTOMERS]: true,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: true,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: true,

    // Shipment Management
    [PERMISSIONS.VIEW_SHIPMENTS]: true,
    [PERMISSIONS.CREATE_SHIPMENTS]: true,
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.EDIT_SHIPMENTS]: true,
    [PERMISSIONS.DELETE_SHIPMENTS]: true,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: true,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: true,
    [PERMISSIONS.EXPORT_SHIPMENTS]: true,
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: true,
    [PERMISSIONS.REVIEW_SHIPMENTS]: true,
    [PERMISSIONS.RESOLVE_BOOKING_RECONCILE]: true,

    // Shipment Document & Action Permissions - Full access for Admin
    [PERMISSIONS.VIEW_DOCUMENTS]: true,
    [PERMISSIONS.VIEW_BOL]: true,
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: true,
    [PERMISSIONS.VIEW_FOLLOW_UPS]: true,
    [PERMISSIONS.ARCHIVE_SHIPMENT]: true,
    [PERMISSIONS.CANCEL_SHIPMENT]: true,
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: true,

    // Financial Information - Full access for Admin
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: true,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: true,

    // Address Management
    [PERMISSIONS.VIEW_ADDRESSES]: true,
    [PERMISSIONS.CREATE_ADDRESSES]: true,
    [PERMISSIONS.EDIT_ADDRESSES]: true,
    [PERMISSIONS.DELETE_ADDRESSES]: true,
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: true,
    [PERMISSIONS.EXPORT_ADDRESSES]: true,
    
    // Billing & Invoicing
    [PERMISSIONS.VIEW_BILLING]: true,
    [PERMISSIONS.CREATE_INVOICES]: true,
    [PERMISSIONS.EDIT_INVOICES]: true,
    [PERMISSIONS.DELETE_INVOICES]: true,
    [PERMISSIONS.VIEW_ALL_INVOICES]: true,
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: true,
    [PERMISSIONS.GENERATE_INVOICES]: true,
    [PERMISSIONS.VIEW_PAYMENTS]: true,
    [PERMISSIONS.MANAGE_AP_PROCESSING]: true,
    
    // Sales Commission Management
    [PERMISSIONS.VIEW_COMMISSIONS]: true,
    [PERMISSIONS.MANAGE_SALES_PERSONS]: true,
    [PERMISSIONS.MANAGE_SALES_TEAMS]: true,
    [PERMISSIONS.CALCULATE_COMMISSIONS]: true,
    [PERMISSIONS.GENERATE_COMMISSION_REPORTS]: true,
    [PERMISSIONS.SCHEDULE_COMMISSION_REPORTS]: true,
    
    // Carrier Management
    [PERMISSIONS.VIEW_CARRIERS]: true,
    [PERMISSIONS.CREATE_CARRIERS]: true,
    [PERMISSIONS.EDIT_CARRIERS]: true,
    [PERMISSIONS.DELETE_CARRIERS]: true,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: true,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: true,
    
    // Reports & Analytics
    [PERMISSIONS.VIEW_REPORTS]: true,
    [PERMISSIONS.CREATE_REPORTS]: true,
    [PERMISSIONS.SCHEDULE_REPORTS]: true,
    [PERMISSIONS.VIEW_ALL_REPORTS]: true,
    [PERMISSIONS.EXPORT_REPORTS]: true,
    [PERMISSIONS.VIEW_ANALYTICS]: true,
    [PERMISSIONS.VIEW_FOLLOWUPS]: true,
    
    // Tracking
    [PERMISSIONS.VIEW_TRACKING]: true,
    [PERMISSIONS.UPDATE_TRACKING]: true,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: true,
    
    // Profile Management
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: true,
    
    // System Settings - Limited (no role management)
    [PERMISSIONS.VIEW_SETTINGS]: true,
    [PERMISSIONS.MANAGE_SETTINGS]: true,
    [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true, // Platform System Settings + System Configuration pages
    [PERMISSIONS.MANAGE_BROKERS]: true, // Customs brokers — admins manage the shared catalog + all companies' brokers
    [PERMISSIONS.MANAGE_ROLES]: false, // Only super admin can manage roles
    [PERMISSIONS.MANAGE_MARKUPS]: true,
    
    // Quote Management
    [PERMISSIONS.CREATE_QUOTES]: true,
    [PERMISSIONS.VIEW_QUOTES]: true,

    // Advanced Features
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.USE_LIVE_RATES]: true,
    [PERMISSIONS.USE_AI_AGENT]: true,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: true,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: true,
    
    // Rate and Pricing Visibility - FULL ACCESS for admins
    [PERMISSIONS.VIEW_RATE_PRICING]: true,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: true,
    [PERMISSIONS.SHOW_RATE_FILTERS]: true,
    
    // Shipment Information Fields - FULL ACCESS for admins
    [PERMISSIONS.VIEW_BILL_TYPE]: true,
    [PERMISSIONS.VIEW_ETA_FIELDS]: true, // Legacy
    [PERMISSIONS.EDIT_ETAS]: true,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: true,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: true,
    
    // QuickShip Form Controls - FULL ACCESS for admins
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: true,
    [PERMISSIONS.SELECT_SHIP_FROM]: true,
    [PERMISSIONS.SELECT_SHIP_TO]: true,
    
    // Address Dropdown Visibility - FULL ACCESS for admins
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: true,
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: true,
    
    // QuickShip Action Controls - FULL ACCESS for admins
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: true,
    [PERMISSIONS.USE_SHIP_LATER]: true,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: true,
    
    // Package Information Fields - FULL ACCESS for admins
    [PERMISSIONS.VIEW_DECLARED_VALUE]: true,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: true,
    
    // Hierarchy Permissions - Administrative access (cannot delete)
    [PERMISSIONS.VIEW_HIERARCHY]: true,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: true,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: true,
    [PERMISSIONS.CREATE_DIVISIONS]: true,
    [PERMISSIONS.EDIT_DIVISIONS]: true,
    [PERMISSIONS.DELETE_DIVISIONS]: false, // Only super admin can delete
    [PERMISSIONS.MOVE_DIVISIONS]: true,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: true,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: true,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: true,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false, // Only super admin can manage access
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: true,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: true,
  },
  
  [ROLES.USER]: { // Company Admin
    // Dashboard & General Access
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true, // Enable admin access - data filtered to their company
    
    // User Management - Limited to their company
    [PERMISSIONS.VIEW_USERS]: false,
    [PERMISSIONS.CREATE_USERS]: false,
    [PERMISSIONS.EDIT_USERS]: false,
    [PERMISSIONS.DELETE_USERS]: false,
    [PERMISSIONS.MANAGE_USER_ROLES]: false,
    [PERMISSIONS.INVITE_USERS]: false,
    [PERMISSIONS.RESET_USER_PASSWORD]: false,
    
    // Company Management - Can only view/edit their own company
    [PERMISSIONS.VIEW_COMPANIES]: true, // Only their company
    [PERMISSIONS.CREATE_COMPANIES]: false,
    [PERMISSIONS.EDIT_COMPANIES]: true, // Only their company
    [PERMISSIONS.DELETE_COMPANIES]: false,
    [PERMISSIONS.VIEW_ALL_COMPANIES]: false,
    
    // Organization Management
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,
    
    // Shipment Management - Only their company's shipments
    [PERMISSIONS.VIEW_SHIPMENTS]: true,
    [PERMISSIONS.CREATE_SHIPMENTS]: true,
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.EDIT_SHIPMENTS]: true,
    [PERMISSIONS.DELETE_SHIPMENTS]: true,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: false,
    [PERMISSIONS.EXPORT_SHIPMENTS]: true,
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: true,
    [PERMISSIONS.REVIEW_SHIPMENTS]: true,
    
    // Shipment Document & Action Permissions - Full access for Company Admin
    [PERMISSIONS.VIEW_DOCUMENTS]: true,
    [PERMISSIONS.VIEW_BOL]: true,
    [PERMISSIONS.VIEW_FOLLOW_UPS]: true,
    [PERMISSIONS.ARCHIVE_SHIPMENT]: true,
    [PERMISSIONS.CANCEL_SHIPMENT]: true,
    
    // Financial Information - Company admins see what their shipments are charged
    // (customerCharges), never our carrier cost, markup or profit (Tyler 2026-10-26).
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: false,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: false,
    
    // Carrier Confirmations print our buy rate and the sub-carrier: hidden, as for company staff
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: false,
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: false,
    
    // Customer Management - Only their company's customers
    [PERMISSIONS.VIEW_CUSTOMERS]: true,
    [PERMISSIONS.CREATE_CUSTOMERS]: true,
    [PERMISSIONS.EDIT_CUSTOMERS]: true,
    [PERMISSIONS.DELETE_CUSTOMERS]: true,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: false,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: true,
    
    // Address Book Management - Only their company's addresses
    [PERMISSIONS.VIEW_ADDRESSES]: true,
    [PERMISSIONS.CREATE_ADDRESSES]: true,
    [PERMISSIONS.EDIT_ADDRESSES]: true,
    [PERMISSIONS.DELETE_ADDRESSES]: true,
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: false,
    [PERMISSIONS.EXPORT_ADDRESSES]: true,
    
    // Billing & Invoicing - Only their company's billing
    [PERMISSIONS.VIEW_BILLING]: true,
    [PERMISSIONS.CREATE_INVOICES]: false,
    [PERMISSIONS.EDIT_INVOICES]: false,
    [PERMISSIONS.DELETE_INVOICES]: false,
    [PERMISSIONS.VIEW_ALL_INVOICES]: false,
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: false,
    [PERMISSIONS.GENERATE_INVOICES]: false,
    
    // Carrier Management - View only
    [PERMISSIONS.VIEW_CARRIERS]: false,
    [PERMISSIONS.CREATE_CARRIERS]: false,
    [PERMISSIONS.EDIT_CARRIERS]: false,
    [PERMISSIONS.DELETE_CARRIERS]: false,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: false,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: false,
    
    // Reports & Analytics - Only their company's data
    [PERMISSIONS.VIEW_REPORTS]: true,
    [PERMISSIONS.CREATE_REPORTS]: true,
    [PERMISSIONS.SCHEDULE_REPORTS]: true,
    [PERMISSIONS.VIEW_ALL_REPORTS]: false,
    [PERMISSIONS.EXPORT_REPORTS]: true,
    [PERMISSIONS.VIEW_ANALYTICS]: true,
    [PERMISSIONS.VIEW_FOLLOWUPS]: true, // Company admins can view/manage follow-ups
    
    // Tracking
    [PERMISSIONS.VIEW_TRACKING]: true,
    [PERMISSIONS.UPDATE_TRACKING]: false,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: true, // Company admins can override status for their shipments
    
    // Profile Management
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: true,
    
    // System Settings
    [PERMISSIONS.VIEW_SETTINGS]: true, // Allow company admins to view settings
    [PERMISSIONS.MANAGE_SETTINGS]: true, // Allow company admins to manage settings (shipment statuses)
    [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: false, // Platform System Settings/Configuration pages are admin/superadmin only
    [PERMISSIONS.MANAGE_BROKERS]: true, // Customs brokers — company admins manage their OWN company's brokers (backend scopes by companyID)
    [PERMISSIONS.MANAGE_ROLES]: false,
    [PERMISSIONS.MANAGE_MARKUPS]: false,
    
    // Quote Management
    [PERMISSIONS.CREATE_QUOTES]: true,
    [PERMISSIONS.VIEW_QUOTES]: true,

    // Advanced Features
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.USE_LIVE_RATES]: true,
    [PERMISSIONS.USE_AI_AGENT]: true,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: true,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: false,
    
    // Rate and Pricing Visibility - FULL ACCESS for company admins
    [PERMISSIONS.VIEW_RATE_PRICING]: true,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: true,
    [PERMISSIONS.SHOW_RATE_FILTERS]: true,
    
    // Shipment Information Fields - FULL ACCESS for company admins
    [PERMISSIONS.VIEW_BILL_TYPE]: true,
    [PERMISSIONS.VIEW_ETA_FIELDS]: true, // Legacy
    [PERMISSIONS.EDIT_ETAS]: true,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: true,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: true,
    
    // QuickShip Form Controls - FULL ACCESS for company admins
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: true,
    [PERMISSIONS.SELECT_SHIP_FROM]: true,
    [PERMISSIONS.SELECT_SHIP_TO]: true,
    
    // Address Dropdown Visibility - FULL ACCESS for company admins
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: true,
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: true,
    
    // QuickShip Action Controls - FULL ACCESS for company admins
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: true,
    [PERMISSIONS.USE_SHIP_LATER]: true,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: true,
    
    // Package Information Fields - FULL ACCESS for company admins
    [PERMISSIONS.VIEW_DECLARED_VALUE]: true,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: true,
    
    // Hierarchy Permissions - Limited for company admins
    // Can view hierarchy and create divisions if explicitly enabled on their user profile
    [PERMISSIONS.VIEW_HIERARCHY]: true,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: true,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: true, // Can see data from child divisions
    [PERMISSIONS.CREATE_DIVISIONS]: true, // Controlled by canCreateSubDivisions user flag
    [PERMISSIONS.EDIT_DIVISIONS]: true, // Only for their divisions
    [PERMISSIONS.DELETE_DIVISIONS]: false,
    [PERMISSIONS.MOVE_DIVISIONS]: false,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: false,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: false,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: false,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: true, // Can view reports for their branch
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: true,
  },
  
  [ROLES.ACCOUNTING]: { // Accounting role - focused on billing and invoicing
    // Dashboard & General Access
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true,
    
    // User Management - Limited
    [PERMISSIONS.VIEW_USERS]: false,
    [PERMISSIONS.CREATE_USERS]: false,
    [PERMISSIONS.EDIT_USERS]: false,
    [PERMISSIONS.DELETE_USERS]: false,
    [PERMISSIONS.MANAGE_USER_ROLES]: false,
    [PERMISSIONS.INVITE_USERS]: false,
    [PERMISSIONS.RESET_USER_PASSWORD]: false,
    
    // Company Management - View only for context
    [PERMISSIONS.VIEW_COMPANIES]: true,
    [PERMISSIONS.CREATE_COMPANIES]: false,
    [PERMISSIONS.EDIT_COMPANIES]: false,
    [PERMISSIONS.DELETE_COMPANIES]: false,
    [PERMISSIONS.VIEW_ALL_COMPANIES]: true,
    
    // Organization Management
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,
    
    // Shipment Management - View only for billing context
    [PERMISSIONS.VIEW_SHIPMENTS]: true,
    [PERMISSIONS.CREATE_SHIPMENTS]: false,
    [PERMISSIONS.EDIT_SHIPMENTS]: false,
    [PERMISSIONS.DELETE_SHIPMENTS]: false,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: true,
    [PERMISSIONS.EXPORT_SHIPMENTS]: true,
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: false,
    [PERMISSIONS.REVIEW_SHIPMENTS]: false,
    
    // Shipment Document & Action Permissions - View only for accounting
    [PERMISSIONS.VIEW_DOCUMENTS]: true, // Can view documents for billing purposes
    [PERMISSIONS.VIEW_BOL]: true, // Can view BOL for billing purposes
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: true, // Can view confirmations for billing context
    [PERMISSIONS.VIEW_FOLLOW_UPS]: false, // No follow-up management
    [PERMISSIONS.ARCHIVE_SHIPMENT]: false, // Cannot archive shipments
    [PERMISSIONS.CANCEL_SHIPMENT]: false, // Cannot cancel shipments
    
    // Financial Information - Accounting needs to see costs for billing
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: true,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: true,
    
    // Carrier Confirmations - Can view but not generate (read-only for billing context)
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: false,
    
    // Customer Management - View only for billing context
    [PERMISSIONS.VIEW_CUSTOMERS]: true,
    [PERMISSIONS.CREATE_CUSTOMERS]: false,
    [PERMISSIONS.EDIT_CUSTOMERS]: false,
    [PERMISSIONS.DELETE_CUSTOMERS]: false,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: true,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: false, // Accounting doesn't need to set defaults
    
    // Address Book Management - View only for billing context
    [PERMISSIONS.VIEW_ADDRESSES]: true,
    [PERMISSIONS.CREATE_ADDRESSES]: false,
    [PERMISSIONS.EDIT_ADDRESSES]: false,
    [PERMISSIONS.DELETE_ADDRESSES]: false,
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: true,
    [PERMISSIONS.EXPORT_ADDRESSES]: true,
    
    // Billing & Invoicing - Full access (the "universe of accounting")
    [PERMISSIONS.VIEW_BILLING]: true,
    [PERMISSIONS.CREATE_INVOICES]: true,
    [PERMISSIONS.EDIT_INVOICES]: true,
    [PERMISSIONS.DELETE_INVOICES]: true,
    [PERMISSIONS.VIEW_ALL_INVOICES]: true,
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: true,
    [PERMISSIONS.GENERATE_INVOICES]: true,
    [PERMISSIONS.VIEW_PAYMENTS]: true,          // Received Payments tab + /admin/billing/payments route
    [PERMISSIONS.MANAGE_AP_PROCESSING]: true,   // AP Processing tab + /admin/billing/ap-processing route

    // Sales Commissions (Billing > Sales Commissions tab + /admin/billing/commissions route)
    [PERMISSIONS.VIEW_COMMISSIONS]: true,
    [PERMISSIONS.MANAGE_SALES_PERSONS]: true,
    [PERMISSIONS.MANAGE_SALES_TEAMS]: true,
    [PERMISSIONS.CALCULATE_COMMISSIONS]: true,
    [PERMISSIONS.GENERATE_COMMISSION_REPORTS]: true,
    [PERMISSIONS.SCHEDULE_COMMISSION_REPORTS]: true,

    // Carrier Management - HIDDEN for accounting (no Carriers menu / Markups; a
    // finance role does not manage carrier integrations or pricing rules)
    [PERMISSIONS.VIEW_CARRIERS]: false,
    [PERMISSIONS.CREATE_CARRIERS]: false,
    [PERMISSIONS.EDIT_CARRIERS]: false,
    [PERMISSIONS.DELETE_CARRIERS]: false,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: false,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: false,

    // Reports & Analytics - Financial reports
    [PERMISSIONS.VIEW_REPORTS]: true,
    [PERMISSIONS.CREATE_REPORTS]: true,
    [PERMISSIONS.SCHEDULE_REPORTS]: true,
    [PERMISSIONS.VIEW_ALL_REPORTS]: true,
    [PERMISSIONS.EXPORT_REPORTS]: true,
    [PERMISSIONS.VIEW_ANALYTICS]: true,
    
    // Tracking - View only
    [PERMISSIONS.VIEW_TRACKING]: true,
    [PERMISSIONS.UPDATE_TRACKING]: false,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: false, // Accounting users cannot override shipment status
    
    // Profile Management
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: true,
    
    // System Settings - Limited
    [PERMISSIONS.VIEW_SETTINGS]: false,
    [PERMISSIONS.MANAGE_SETTINGS]: false,
    [PERMISSIONS.MANAGE_ROLES]: false,
    [PERMISSIONS.MANAGE_MARKUPS]: false, // Carrier/pricing markups live under the Carriers menu — hidden for accounting

    // Quote Management - accounting may VIEW quotes (billing context) but does not
    // create shipments or quotes
    [PERMISSIONS.CREATE_QUOTES]: false,
    [PERMISSIONS.VIEW_QUOTES]: true,

    // Advanced Features
    [PERMISSIONS.USE_QUICKSHIP]: false,
    [PERMISSIONS.USE_LIVE_RATES]: false, // No "New Shipment" (live-rate creation) for accounting
    [PERMISSIONS.USE_AI_AGENT]: false,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: false,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: false,
    
    // Rate and Pricing Visibility - FULL ACCESS for accounting (they need pricing info)
    [PERMISSIONS.VIEW_RATE_PRICING]: true,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: true,
    [PERMISSIONS.SHOW_RATE_FILTERS]: true,
    
    // Shipment Information Fields - FULL ACCESS for accounting
    [PERMISSIONS.VIEW_BILL_TYPE]: true,
    [PERMISSIONS.VIEW_ETA_FIELDS]: true, // Legacy
    [PERMISSIONS.EDIT_ETAS]: true,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: true,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: true,
    
    // QuickShip Form Controls - FULL ACCESS for accounting
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: true,
    [PERMISSIONS.SELECT_SHIP_FROM]: true,
    [PERMISSIONS.SELECT_SHIP_TO]: true,
    
    // Address Dropdown Visibility - FULL ACCESS for accounting
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: true,
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: true,
    
    // QuickShip Action Controls - FULL ACCESS for accounting
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: true,
    [PERMISSIONS.USE_SHIP_LATER]: true,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: true,
    
    // Package Information Fields - FULL ACCESS for accounting
    [PERMISSIONS.VIEW_DECLARED_VALUE]: true,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: true,
    
    // Hierarchy Permissions - Limited for accounting (view only for billing context)
    [PERMISSIONS.VIEW_HIERARCHY]: true,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: true,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: true, // Need to see all data for billing
    [PERMISSIONS.CREATE_DIVISIONS]: false,
    [PERMISSIONS.EDIT_DIVISIONS]: false,
    [PERMISSIONS.DELETE_DIVISIONS]: false,
    [PERMISSIONS.MOVE_DIVISIONS]: false,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: false,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: false,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: false,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: true,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: true,
  },
  
  [ROLES.COMPANY_STAFF]: { // Company Staff - basic operational access
    // Dashboard & General Access
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    // /admin is now the universal home for every role; feature access stays gated below.
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true,
    
    // User Management - No access
    [PERMISSIONS.VIEW_USERS]: false,
    [PERMISSIONS.CREATE_USERS]: false,
    [PERMISSIONS.EDIT_USERS]: false,
    [PERMISSIONS.DELETE_USERS]: false,
    [PERMISSIONS.MANAGE_USER_ROLES]: false,
    [PERMISSIONS.INVITE_USERS]: false,
    [PERMISSIONS.RESET_USER_PASSWORD]: false,
    
    // Company Management - View only their company
    [PERMISSIONS.VIEW_COMPANIES]: true, // Only their company
    [PERMISSIONS.CREATE_COMPANIES]: false,
    [PERMISSIONS.EDIT_COMPANIES]: false,
    [PERMISSIONS.DELETE_COMPANIES]: false,
    [PERMISSIONS.VIEW_ALL_COMPANIES]: false,
    
    // Organization Management
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,
    
    // Shipment Management - Basic operations only
    [PERMISSIONS.VIEW_SHIPMENTS]: true,
    [PERMISSIONS.CREATE_SHIPMENTS]: true,
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.EDIT_SHIPMENTS]: false, // Cannot edit existing shipments
    [PERMISSIONS.DELETE_SHIPMENTS]: false,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: false,
    [PERMISSIONS.EXPORT_SHIPMENTS]: true,
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: true,
    [PERMISSIONS.REVIEW_SHIPMENTS]: true,
    
    // Shipment Document & Action Permissions - Limited access for company staff
    [PERMISSIONS.VIEW_DOCUMENTS]: false, // Cannot view documents section
    [PERMISSIONS.VIEW_BOL]: true, // Can view BOL documents
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: false, // Cannot view carrier confirmations
    [PERMISSIONS.VIEW_FOLLOW_UPS]: true, // Can view follow-up tasks
    [PERMISSIONS.ARCHIVE_SHIPMENT]: false, // Cannot archive shipments
    [PERMISSIONS.CANCEL_SHIPMENT]: false, // Cannot cancel shipments
    
    // Financial Information - RESTRICTED: Company staff cannot see costs/profits
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: false,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: false,
    
    // Carrier Confirmations - RESTRICTED: Company staff cannot view or manage carrier confirmations
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: false,
    
    // Customer Management - View and create only
    [PERMISSIONS.VIEW_CUSTOMERS]: true,
    [PERMISSIONS.CREATE_CUSTOMERS]: true,
    [PERMISSIONS.EDIT_CUSTOMERS]: false,
    [PERMISSIONS.DELETE_CUSTOMERS]: false,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: false,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: false, // Company staff cannot set defaults
    
    // Address Book Management - View and create only
    [PERMISSIONS.VIEW_ADDRESSES]: true,
    [PERMISSIONS.CREATE_ADDRESSES]: true,
    [PERMISSIONS.EDIT_ADDRESSES]: false,
    [PERMISSIONS.DELETE_ADDRESSES]: false,
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: false,
    [PERMISSIONS.EXPORT_ADDRESSES]: true,
    
    // Billing & Invoicing - View only
    [PERMISSIONS.VIEW_BILLING]: true,
    [PERMISSIONS.CREATE_INVOICES]: false,
    [PERMISSIONS.EDIT_INVOICES]: false,
    [PERMISSIONS.DELETE_INVOICES]: false,
    [PERMISSIONS.VIEW_ALL_INVOICES]: false,
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: false,
    [PERMISSIONS.GENERATE_INVOICES]: false,
    
    // Carrier Management - View only
    [PERMISSIONS.VIEW_CARRIERS]: true,
    [PERMISSIONS.CREATE_CARRIERS]: false,
    [PERMISSIONS.EDIT_CARRIERS]: false,
    [PERMISSIONS.DELETE_CARRIERS]: false,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: false,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: false,
    
    // Reports & Analytics - Basic reports only
    [PERMISSIONS.VIEW_REPORTS]: true,
    [PERMISSIONS.CREATE_REPORTS]: false,
    [PERMISSIONS.SCHEDULE_REPORTS]: false,
    [PERMISSIONS.VIEW_ALL_REPORTS]: false,
    [PERMISSIONS.EXPORT_REPORTS]: true,
    [PERMISSIONS.VIEW_ANALYTICS]: false,
    
    // Tracking
    [PERMISSIONS.VIEW_TRACKING]: true,
    [PERMISSIONS.UPDATE_TRACKING]: false,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: false, // Company staff cannot override shipment status
    
    // Profile Management
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: false,
    
    // System Settings
    [PERMISSIONS.VIEW_SETTINGS]: false,
    [PERMISSIONS.MANAGE_SETTINGS]: false,
    [PERMISSIONS.MANAGE_ROLES]: false,
    [PERMISSIONS.MANAGE_MARKUPS]: false,
    
    // Quote Management
    [PERMISSIONS.CREATE_QUOTES]: true,
    [PERMISSIONS.VIEW_QUOTES]: true,

    // Advanced Features
    [PERMISSIONS.USE_QUICKSHIP]: true,
    [PERMISSIONS.USE_LIVE_RATES]: true,
    [PERMISSIONS.USE_AI_AGENT]: false,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: false,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: false,
    
    // Rate and Pricing Visibility - operational view but filters hidden by default
    [PERMISSIONS.VIEW_RATE_PRICING]: true,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: true,
    [PERMISSIONS.SHOW_RATE_FILTERS]: false,
    
    // Shipment Information Fields - FULL ACCESS for company staff
    [PERMISSIONS.VIEW_BILL_TYPE]: true,
    [PERMISSIONS.VIEW_ETA_FIELDS]: true, // Legacy
    [PERMISSIONS.EDIT_ETAS]: true,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: true,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: true,
    
    // QuickShip Form Controls - FULL ACCESS for company staff
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: true,
    [PERMISSIONS.SELECT_SHIP_FROM]: true,
    [PERMISSIONS.SELECT_SHIP_TO]: true,
    
    // Address Dropdown Visibility - RESTRICTED for company staff
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: true, // Can view but with limitations
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: true, // Can view but with limitations
    
    // QuickShip Action Controls - FULL ACCESS for company staff
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: true,
    [PERMISSIONS.USE_SHIP_LATER]: true,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: true,
    
    // Package Information Fields - FULL ACCESS for company staff
    [PERMISSIONS.VIEW_DECLARED_VALUE]: true,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: true,
    
    // Hierarchy Permissions - Very limited for company staff
    [PERMISSIONS.VIEW_HIERARCHY]: false, // Cannot view hierarchy structure
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: false,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: false, // Only their own company's data
    [PERMISSIONS.CREATE_DIVISIONS]: false,
    [PERMISSIONS.EDIT_DIVISIONS]: false,
    [PERMISSIONS.DELETE_DIVISIONS]: false,
    [PERMISSIONS.MOVE_DIVISIONS]: false,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: false,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: false,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: false,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: false,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: false,
  },

  // NEW: MANUFACTURER ROLE - Limited access for manufacturing partners
  [ROLES.MANUFACTURER]: {
    // Dashboard & General Access - LIMITED: Read-only dashboard access
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    // /admin is now the universal home for every role; feature access stays gated below.
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true,
    
    // User Management - sees their own customer's users under Access > Users and can
    // add users there at their role or below (TEAM_INVITE_ROLES). No edit/delete.
    [PERMISSIONS.VIEW_USERS]: true,
    [PERMISSIONS.CREATE_USERS]: false,
    [PERMISSIONS.EDIT_USERS]: false,
    [PERMISSIONS.DELETE_USERS]: false,
    [PERMISSIONS.MANAGE_USER_ROLES]: false,
    [PERMISSIONS.INVITE_USERS]: true,
    [PERMISSIONS.RESET_USER_PASSWORD]: false,
    
    // Company Management - RESTRICTED: No company management
    [PERMISSIONS.VIEW_COMPANIES]: false,
    [PERMISSIONS.CREATE_COMPANIES]: false,
    [PERMISSIONS.EDIT_COMPANIES]: false,
    [PERMISSIONS.DELETE_COMPANIES]: false,
    [PERMISSIONS.VIEW_ALL_COMPANIES]: false,
    
    // Organization Management - RESTRICTED: No organization management
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,
    
    // Shipment Management - LIMITED: Can create and view shipments but not edit existing ones
    [PERMISSIONS.VIEW_SHIPMENTS]: true, // Can view shipments they're involved in
    [PERMISSIONS.CREATE_SHIPMENTS]: true, // Can create new shipments (but not QuickShip)
    [PERMISSIONS.USE_QUICKSHIP]: false, // Cannot use QuickShip - only regular shipment creation
    [PERMISSIONS.EDIT_SHIPMENTS]: false, // Cannot edit shipments
    [PERMISSIONS.DELETE_SHIPMENTS]: false,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: false, // Cannot see all shipments
    [PERMISSIONS.EXPORT_SHIPMENTS]: false,
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: false,
    [PERMISSIONS.REVIEW_SHIPMENTS]: false,
    
    // Shipment Document & Action Permissions - RESTRICTED: No access to document actions
    [PERMISSIONS.VIEW_DOCUMENTS]: false, // Cannot view documents section
    [PERMISSIONS.VIEW_BOL]: false, // Cannot view/print BOL documents
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: false, // Cannot view/print carrier confirmations
    [PERMISSIONS.VIEW_FOLLOW_UPS]: true, // Follow-ups nav item + dashboard widget (gated by view_follow_ups)
    [PERMISSIONS.ARCHIVE_SHIPMENT]: false, // Cannot archive shipments
    [PERMISSIONS.CANCEL_SHIPMENT]: false, // Cannot cancel shipments
    
    // Financial Information - RESTRICTED: No financial access
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: false,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: false,
    
    // Carrier Confirmations - RESTRICTED: No carrier confirmation access
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: false,
    
    // Customer Management - SELF-SERVICE: can view + edit their OWN customer
    // record only. The list and detail pages are scoped to the user's
    // connectedCustomers client-side; create / delete / view-all stay off.
    [PERMISSIONS.VIEW_CUSTOMERS]: true,
    [PERMISSIONS.CREATE_CUSTOMERS]: false,
    [PERMISSIONS.EDIT_CUSTOMERS]: true,
    [PERMISSIONS.DELETE_CUSTOMERS]: false,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: false,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: false, // Manufacturers cannot set defaults
    
    // Address Book Management - LIMITED: Basic address management
    [PERMISSIONS.VIEW_ADDRESSES]: true, // Can view their own addresses
    [PERMISSIONS.CREATE_ADDRESSES]: true, // Can create new addresses
    [PERMISSIONS.EDIT_ADDRESSES]: true, // Can edit their own addresses
    [PERMISSIONS.DELETE_ADDRESSES]: false, // Cannot delete addresses
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: false, // Cannot see all addresses
    [PERMISSIONS.EXPORT_ADDRESSES]: false, // Cannot export addresses
    
    // Billing & Invoicing - RESTRICTED: No billing access
    [PERMISSIONS.VIEW_BILLING]: false,
    [PERMISSIONS.CREATE_INVOICES]: false,
    [PERMISSIONS.EDIT_INVOICES]: false,
    [PERMISSIONS.DELETE_INVOICES]: false,
    [PERMISSIONS.VIEW_ALL_INVOICES]: false,
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: false,
    [PERMISSIONS.GENERATE_INVOICES]: false,
    
    // Sales Commission Management - RESTRICTED: No commission access
    [PERMISSIONS.VIEW_COMMISSIONS]: false,
    [PERMISSIONS.MANAGE_SALES_PERSONS]: false,
    [PERMISSIONS.MANAGE_SALES_TEAMS]: false,
    [PERMISSIONS.CALCULATE_COMMISSIONS]: false,
    [PERMISSIONS.GENERATE_COMMISSION_REPORTS]: false,
    [PERMISSIONS.SCHEDULE_COMMISSION_REPORTS]: false,
    
    // Carrier Management - RESTRICTED: No carrier access (hides Carriers nav + blocks /admin/carriers)
    [PERMISSIONS.VIEW_CARRIERS]: false, // Manufacturers should not see carriers
    [PERMISSIONS.CREATE_CARRIERS]: false,
    [PERMISSIONS.EDIT_CARRIERS]: false,
    [PERMISSIONS.DELETE_CARRIERS]: false,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: false,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: false,
    [PERMISSIONS.MANAGE_CARRIERS]: false,
    
    // Address Management - LIMITED: Basic address book access
    [PERMISSIONS.MANAGE_ADDRESSES]: true,
    
    // Broker Management - RESTRICTED: No broker management  
    [PERMISSIONS.MANAGE_BROKERS]: false,
    
    // Reports & Analytics - LIMITED: Basic tracking reports only
    [PERMISSIONS.VIEW_REPORTS]: false,
    [PERMISSIONS.CREATE_REPORTS]: false,
    [PERMISSIONS.SCHEDULE_REPORTS]: false,
    [PERMISSIONS.VIEW_ALL_REPORTS]: false,
    [PERMISSIONS.EXPORT_REPORTS]: false,
    [PERMISSIONS.VIEW_ANALYTICS]: false,
    [PERMISSIONS.VIEW_FOLLOWUPS]: true, // /admin/followups route gate (distinct key from view_follow_ups used by the nav item)

    // Tracking - LIMITED: Read-only tracking for assigned shipments
    [PERMISSIONS.VIEW_TRACKING]: true, // Can track shipments they're involved in
    [PERMISSIONS.UPDATE_TRACKING]: false, // Cannot update tracking status
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: false, // Cannot override shipment status
    
    // Profile Management - LIMITED: Can view and edit own profile only
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management - LIMITED: View notifications, no management
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: false,
    
    // System Settings - RESTRICTED: No system access
    [PERMISSIONS.VIEW_SETTINGS]: false,
    [PERMISSIONS.MANAGE_SETTINGS]: false,
    [PERMISSIONS.MANAGE_ROLES]: false,
    [PERMISSIONS.MANAGE_MARKUPS]: false,
    
    // Quote Management - Spot Quote / Quick Quote enabled for manufacturers
    [PERMISSIONS.CREATE_QUOTES]: true, // Spot Quote (+NEW button) + Quick Quote nav dropdown
    [PERMISSIONS.VIEW_QUOTES]: true,

    // Advanced Features - Live Rates enabled (Rated Shipment); QuickShip stays restricted
    [PERMISSIONS.USE_QUICKSHIP]: false,
    [PERMISSIONS.USE_LIVE_RATES]: true, // Rated Shipment (+NEW button) + New Shipment nav dropdown
    [PERMISSIONS.USE_AI_AGENT]: false,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: false,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: false,
    
    // Rate and Pricing Visibility - RESTRICTED: No pricing visibility
    [PERMISSIONS.VIEW_RATE_PRICING]: false,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: false,
    [PERMISSIONS.SHOW_RATE_FILTERS]: false,
    
    // Shipment Information Fields - RESTRICTED: Limited field access
    [PERMISSIONS.VIEW_BILL_TYPE]: false,
    [PERMISSIONS.VIEW_ETA_FIELDS]: false, // Legacy
    [PERMISSIONS.EDIT_ETAS]: false,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: false,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: false,
    
    // QuickShip Form Controls - RESTRICTED: No QuickShip access
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: false,
    [PERMISSIONS.SELECT_SHIP_FROM]: false,
    [PERMISSIONS.SELECT_SHIP_TO]: false,
    
    // Address Dropdown Visibility - RESTRICTED: No address dropdown access
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: false,
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: false,
    
    // QuickShip Action Controls - RESTRICTED: No QuickShip actions
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: false,
    [PERMISSIONS.USE_SHIP_LATER]: false,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: false,
    
    // Package Information Fields - RESTRICTED: Limited package details
    [PERMISSIONS.VIEW_DECLARED_VALUE]: false,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: false,
    
    // Hierarchy Permissions - RESTRICTED: No hierarchy access for manufacturers
    [PERMISSIONS.VIEW_HIERARCHY]: false,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: false,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: false,
    [PERMISSIONS.CREATE_DIVISIONS]: false,
    [PERMISSIONS.EDIT_DIVISIONS]: false,
    [PERMISSIONS.DELETE_DIVISIONS]: false,
    [PERMISSIONS.MOVE_DIVISIONS]: false,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: false,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: false,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: false,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: false,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: false,
  },

  // CUSTOMER ROLE - End-user customer with limited admin access
  // Can only view their own customer's dashboard, shipments, and invoices
  [ROLES.CUSTOMER]: {
    // Dashboard & General Access - LIMITED: Admin dashboard with customer-filtered data
    [PERMISSIONS.VIEW_DASHBOARD]: true,
    [PERMISSIONS.VIEW_ADMIN_DASHBOARD]: true, // Access to admin, but data is filtered to their customer
    
    // User Management - RESTRICTED: No user management
    [PERMISSIONS.VIEW_USERS]: false,
    [PERMISSIONS.CREATE_USERS]: false,
    [PERMISSIONS.EDIT_USERS]: false,
    [PERMISSIONS.DELETE_USERS]: false,
    [PERMISSIONS.MANAGE_USER_ROLES]: false,
    [PERMISSIONS.INVITE_USERS]: false,
    [PERMISSIONS.RESET_USER_PASSWORD]: false,
    
    // Company Management - RESTRICTED: No company management
    [PERMISSIONS.VIEW_COMPANIES]: false,
    [PERMISSIONS.CREATE_COMPANIES]: false,
    [PERMISSIONS.EDIT_COMPANIES]: false,
    [PERMISSIONS.DELETE_COMPANIES]: false,
    [PERMISSIONS.VIEW_ALL_COMPANIES]: false,
    
    // Organization Management - RESTRICTED: No organization management
    [PERMISSIONS.VIEW_ORGANIZATIONS]: false,
    [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
    [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
    [PERMISSIONS.DELETE_ORGANIZATIONS]: false,
    
    // Shipment Management - Can view and create rated shipments only
    [PERMISSIONS.VIEW_SHIPMENTS]: true, // View their customer's shipments only
    [PERMISSIONS.CREATE_SHIPMENTS]: true, // Can create rated shipments
    [PERMISSIONS.USE_QUICKSHIP]: false, // Cannot use QuickShip (manual entry)
    [PERMISSIONS.EDIT_SHIPMENTS]: false, // Cannot edit shipments
    [PERMISSIONS.DELETE_SHIPMENTS]: false,
    [PERMISSIONS.VIEW_ALL_SHIPMENTS]: false, // Only their customer's shipments
    [PERMISSIONS.EXPORT_SHIPMENTS]: true, // Can export their shipments
    [PERMISSIONS.MANAGE_DRAFT_SHIPMENTS]: false,
    [PERMISSIONS.REVIEW_SHIPMENTS]: false,
    
    // Shipment Document & Action Permissions - LIMITED: View documents only
    [PERMISSIONS.VIEW_DOCUMENTS]: true,
    [PERMISSIONS.VIEW_BOL]: true,
    [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: false, // Hide carrier confirmations from customer portal
    [PERMISSIONS.VIEW_FOLLOW_UPS]: false,
    [PERMISSIONS.ARCHIVE_SHIPMENT]: false,
    [PERMISSIONS.CANCEL_SHIPMENT]: false,
    
    // Financial Information - RESTRICTED: No cost/profit visibility
    [PERMISSIONS.VIEW_SHIPMENT_COSTS]: false,
    [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: false,
    
    // Carrier Confirmations - No access
    [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: false,
    
    // Customer Management - RESTRICTED: No customer management
    [PERMISSIONS.VIEW_CUSTOMERS]: false,
    [PERMISSIONS.CREATE_CUSTOMERS]: false,
    [PERMISSIONS.EDIT_CUSTOMERS]: false,
    [PERMISSIONS.DELETE_CUSTOMERS]: false,
    [PERMISSIONS.VIEW_ALL_CUSTOMERS]: false,
    [PERMISSIONS.SET_DEFAULT_ADDRESSES]: false,
    
    // Address Book Management - VIEW ONLY: Customers can browse their own
    // address book (their shipping locations), but cannot mutate it.
    [PERMISSIONS.VIEW_ADDRESSES]: true,
    [PERMISSIONS.CREATE_ADDRESSES]: false,
    [PERMISSIONS.EDIT_ADDRESSES]: false,
    [PERMISSIONS.DELETE_ADDRESSES]: false,
    [PERMISSIONS.VIEW_ALL_ADDRESSES]: false,
    [PERMISSIONS.EXPORT_ADDRESSES]: false,
    
    // Billing & Invoicing - VIEW ONLY: Can view their customer's invoices
    [PERMISSIONS.VIEW_BILLING]: true, // View their invoices only
    [PERMISSIONS.CREATE_INVOICES]: false,
    [PERMISSIONS.EDIT_INVOICES]: false,
    [PERMISSIONS.DELETE_INVOICES]: false,
    [PERMISSIONS.VIEW_ALL_INVOICES]: false, // Only their customer's invoices
    [PERMISSIONS.MANAGE_PAYMENT_TERMS]: false,
    [PERMISSIONS.GENERATE_INVOICES]: false,
    [PERMISSIONS.VIEW_PAYMENTS]: true, // Can view payment history for their invoices
    [PERMISSIONS.MANAGE_AP_PROCESSING]: false,
    
    // Sales Commission Management - RESTRICTED
    [PERMISSIONS.VIEW_COMMISSIONS]: false,
    [PERMISSIONS.MANAGE_SALES_PERSONS]: false,
    [PERMISSIONS.MANAGE_SALES_TEAMS]: false,
    [PERMISSIONS.CALCULATE_COMMISSIONS]: false,
    [PERMISSIONS.GENERATE_COMMISSION_REPORTS]: false,
    [PERMISSIONS.SCHEDULE_COMMISSION_REPORTS]: false,
    
    // Carrier Management - RESTRICTED
    [PERMISSIONS.VIEW_CARRIERS]: false,
    [PERMISSIONS.CREATE_CARRIERS]: false,
    [PERMISSIONS.EDIT_CARRIERS]: false,
    [PERMISSIONS.DELETE_CARRIERS]: false,
    [PERMISSIONS.MANAGE_CARRIER_KEYS]: false,
    [PERMISSIONS.MANAGE_EDI_MAPPING]: false,
    [PERMISSIONS.MANAGE_CARRIERS]: false,
    
    // Address Management - RESTRICTED
    [PERMISSIONS.MANAGE_ADDRESSES]: false,
    
    // Broker Management - RESTRICTED
    [PERMISSIONS.MANAGE_BROKERS]: false,
    
    // Reports & Analytics - LIMITED: Basic tracking reports only
    [PERMISSIONS.VIEW_REPORTS]: false,
    [PERMISSIONS.CREATE_REPORTS]: false,
    [PERMISSIONS.SCHEDULE_REPORTS]: false,
    [PERMISSIONS.VIEW_ALL_REPORTS]: false,
    [PERMISSIONS.EXPORT_REPORTS]: false,
    [PERMISSIONS.VIEW_ANALYTICS]: false,
    [PERMISSIONS.VIEW_FOLLOWUPS]: false,
    
    // Tracking - VIEW ONLY: Can track their shipments
    [PERMISSIONS.VIEW_TRACKING]: true,
    [PERMISSIONS.UPDATE_TRACKING]: false,
    [PERMISSIONS.MANUAL_STATUS_OVERRIDE]: false,
    
    // Profile Management - Can view and edit own profile
    [PERMISSIONS.VIEW_PROFILE]: true,
    [PERMISSIONS.EDIT_PROFILE]: true,
    
    // Notification Management - LIMITED
    [PERMISSIONS.VIEW_NOTIFICATIONS]: true,
    [PERMISSIONS.MANAGE_NOTIFICATIONS]: false,
    
    // System Settings - RESTRICTED
    [PERMISSIONS.VIEW_SETTINGS]: false,
    [PERMISSIONS.MANAGE_SETTINGS]: false,
    [PERMISSIONS.MANAGE_ROLES]: false,
    [PERMISSIONS.MANAGE_MARKUPS]: false,
    
    // Quote Management - RESTRICTED
    [PERMISSIONS.CREATE_QUOTES]: false,
    [PERMISSIONS.VIEW_QUOTES]: false,

    // Advanced Features - Rated shipments only
    [PERMISSIONS.USE_QUICKSHIP]: false,
    [PERMISSIONS.USE_LIVE_RATES]: true, // Customers can create rated shipments
    [PERMISSIONS.USE_AI_AGENT]: false,
    [PERMISSIONS.USE_ADVANCED_ROUTING]: false,
    [PERMISSIONS.MANAGE_INTEGRATIONS]: false,
    
    // Rate and Pricing Visibility - Customers must see prices to choose a rate
    [PERMISSIONS.VIEW_RATE_PRICING]: true,
    [PERMISSIONS.VIEW_RATE_BREAKDOWN]: false, // Hide detailed cost breakdown (surcharges, fuel, etc.)
    [PERMISSIONS.SHOW_RATE_FILTERS]: true, // Allow filtering rates by carrier/service level

    // Shipment Information Fields - LIMITED
    [PERMISSIONS.VIEW_BILL_TYPE]: false,
    [PERMISSIONS.VIEW_ETA_FIELDS]: true,
    [PERMISSIONS.EDIT_ETAS]: false,
    [PERMISSIONS.EDIT_SHIPMENT_REFERENCES]: false,
    [PERMISSIONS.EDIT_CARRIER_TRACKING]: false,

    // QuickShip Form Controls - RESTRICTED
    [PERMISSIONS.SELECT_QUICKSHIP_CARRIER]: false,
    [PERMISSIONS.SELECT_SHIP_FROM]: false,
    [PERMISSIONS.SELECT_SHIP_TO]: false,
    
    // Address Dropdown Visibility - RESTRICTED
    [PERMISSIONS.VIEW_SHIPFROM_ADDRESSES]: false,
    [PERMISSIONS.VIEW_SHIPTO_ADDRESSES]: false,
    
    // QuickShip Action Controls - RESTRICTED
    [PERMISSIONS.USE_SWITCH_TO_LIVE_RATES]: false,
    [PERMISSIONS.USE_SHIP_LATER]: false,
    [PERMISSIONS.USE_BOOK_SHIPMENT]: false,
    
    // Package Information Fields - LIMITED
    [PERMISSIONS.VIEW_DECLARED_VALUE]: false,
    [PERMISSIONS.VIEW_FREIGHT_CLASS]: false,
    
    // Hierarchy Permissions - RESTRICTED
    [PERMISSIONS.VIEW_HIERARCHY]: false,
    [PERMISSIONS.VIEW_HIERARCHY_TREE]: false,
    [PERMISSIONS.VIEW_DESCENDANT_DATA]: false,
    [PERMISSIONS.CREATE_DIVISIONS]: false,
    [PERMISSIONS.EDIT_DIVISIONS]: false,
    [PERMISSIONS.DELETE_DIVISIONS]: false,
    [PERMISSIONS.MOVE_DIVISIONS]: false,
    [PERMISSIONS.MANAGE_CHILD_COMPANIES]: false,
    [PERMISSIONS.CONFIGURE_BILLING_MODE]: false,
    [PERMISSIONS.CONFIGURE_INHERITANCE]: false,
    [PERMISSIONS.MANAGE_HIERARCHY_ACCESS]: false,
    [PERMISSIONS.VIEW_HIERARCHY_REPORTS]: false,
    [PERMISSIONS.EXPORT_HIERARCHY_DATA]: false,
  },
};

// Organization Admin: everything a Company Admin does, plus what an admin does that stays
// inside the org admin's own companies (Tyler 2026-10-27: "org_admin should be able to do
// everything inside that org"). Its companies are its connectedCompanies plus the
// companies its org member grants reach (users.orgAccess.memberCompanyIDs); the server
// (utils/tenantScope.js) and firestore.rules scope it there like any company user. It is
// not a platform role: invoicing, AP, commissions, markups, carrier configuration, EDI
// mapping, the "view all" grants and creating companies or organizations stay with admin.
export const ORG_ADMIN_PERMISSIONS = Object.freeze({
  ...ROLE_PERMISSIONS[ROLES.USER],

  // Carrier cost, margin and carrier confirmations on its companies' shipments.
  [PERMISSIONS.VIEW_SHIPMENT_COSTS]: true,
  [PERMISSIONS.VIEW_SHIPMENT_FINANCIALS]: true,
  [PERMISSIONS.VIEW_CARRIER_CONFIRMATION]: true,
  [PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS]: true,

  // Users of its own companies (TEAM_INVITE_ROLES; functions/src/teamAccess.js). Deleting
  // an account and resetting a password stay with admin: deactivate instead.
  [PERMISSIONS.VIEW_USERS]: true,
  [PERMISSIONS.CREATE_USERS]: true,
  [PERMISSIONS.EDIT_USERS]: true,
  [PERMISSIONS.INVITE_USERS]: true,
  [PERMISSIONS.MANAGE_USER_ROLES]: true,
  [PERMISSIONS.DELETE_USERS]: false,
  [PERMISSIONS.RESET_USER_PASSWORD]: false,

  // Its organization record (firestore.rules canReadOrganization). Editing stays with admin:
  // the org form loads every company and user and writes their org links, which only an
  // admin may (the rules still let a connected org admin change the record's own fields).
  [PERMISSIONS.VIEW_ORGANIZATIONS]: true,
  [PERMISSIONS.EDIT_ORGANIZATIONS]: false,
  [PERMISSIONS.CREATE_ORGANIZATIONS]: false,
  [PERMISSIONS.DELETE_ORGANIZATIONS]: false,

  // Payments on its companies' invoices (payments/paymentAccess.js scopes by company).
  [PERMISSIONS.VIEW_PAYMENTS]: true,

  // Held by roles it may assign (company_staff, manufacturer), so it holds them too:
  // a team manager's permissions are a superset of every role it hands out.
  [PERMISSIONS.VIEW_CARRIERS]: true,
  [PERMISSIONS.MANAGE_ADDRESSES]: true,
});

ROLE_PERMISSIONS[ROLES.ORG_ADMIN] = ORG_ADMIN_PERMISSIONS;

// The user-management permissions. A manufacturer holds some of them, its staff
// never do, and no role outside USER_MANAGER_ROLES ever does.
export const USER_MANAGEMENT_PERMISSIONS = [
  PERMISSIONS.VIEW_USERS,
  PERMISSIONS.CREATE_USERS,
  PERMISSIONS.EDIT_USERS,
  PERMISSIONS.DELETE_USERS,
  PERMISSIONS.MANAGE_USER_ROLES,
  PERMISSIONS.INVITE_USERS,
  PERMISSIONS.RESET_USER_PASSWORD,
];

// The only roles the server accepts as user managers: ADMIN_ROLES in
// functions/src/admin-user-management.js plus the TEAM_INVITE_ROLES keys (mirrored in
// functions/src/teamAccess.js). Every user-management callable and the users rules
// refuse any other role, so hasPermission() denies the USER_MANAGEMENT_PERMISSIONS to
// it whatever ROLE_PERMISSIONS or a roles/{id} document says.
export const USER_MANAGER_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ...Object.keys(TEAM_INVITE_ROLES)];

// Manufacturer Staff has no permission row of its own: it is the manufacturer's row
// with user management switched off, so it can never exceed the manufacturer who
// invites them. hasPermission() delegates the same way, so role-editor changes to
// the manufacturer role carry over to staff.
ROLE_PERMISSIONS[ROLES.MANUFACTURER_STAFF] = Object.fromEntries(
  Object.entries(ROLE_PERMISSIONS[ROLES.MANUFACTURER])
    .map(([permission, granted]) => [permission, USER_MANAGEMENT_PERMISSIONS.includes(permission) ? false : granted])
);

/**
 * How hasPermission() decides one role/permission pair, given the roles/{id}
 * permission maps (`dbPermissionsOf(roleId)` -> map or null). Pure, so the
 * /admin/role-permissions page shows exactly what a signed-in user of the role gets
 * and why (rolePermissions.explain.test.js holds it equal to hasPermission).
 *
 * source: 'superadmin' | 'locked' (user management the server refuses this role)
 *   | 'code' (no document value: the hardcoded row decides)
 *   | 'db' (a document value equal to the hardcoded row, or any value of a custom role)
 *   | 'db-grant' (a document `true` beyond the hardcoded row)
 *   | 'db-revoke' (a document `false` revoking a hardcoded grant)
 *   | 'db-wildcard' (the document holds '*')
 * Manufacturer Staff answers as the manufacturer, with `inheritedFrom: 'manufacturer'`.
 */
export const explainRolePermission = (roleId, permission, dbPermissionsOf = () => null) => {
  if (!roleId || !permission) return { granted: false, source: 'code', codeDefault: false };
  if (roleId === ROLES.SUPER_ADMIN) return { granted: true, source: 'superadmin', codeDefault: true };

  const isUserManagement = USER_MANAGEMENT_PERMISSIONS.includes(permission);
  if (roleId === ROLES.MANUFACTURER_STAFF) {
    if (isUserManagement) return { granted: false, source: 'locked', codeDefault: false };
    return { ...explainRolePermission(ROLES.MANUFACTURER, permission, dbPermissionsOf), inheritedFrom: ROLES.MANUFACTURER };
  }
  if (isUserManagement && !USER_MANAGER_ROLES.includes(roleId)) {
    return { granted: false, source: 'locked', codeDefault: false };
  }

  const code = Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, roleId) ? ROLE_PERMISSIONS[roleId] : null;
  const codeDefault = !!code && (code['*'] === true || code[permission] === true);
  const db = dbPermissionsOf(roleId) || {};
  const dbValue = Object.prototype.hasOwnProperty.call(db, permission) ? db[permission] : undefined;

  if (db['*'] === true) return { granted: true, source: 'db-wildcard', codeDefault, dbValue };
  if (code && code['*'] === true) return { granted: true, source: 'code', codeDefault, dbValue };
  if (dbValue === true) return { granted: true, source: !code || codeDefault ? 'db' : 'db-grant', codeDefault, dbValue };
  if (dbValue === false) return { granted: false, source: !code || !codeDefault ? 'db' : 'db-revoke', codeDefault, dbValue };
  return { granted: codeDefault, source: 'code', codeDefault, dbValue };
};

// Import dynamic role service (lazy import to avoid circular dependencies)
let roleServicePromise = null;
let roleServiceInstance = null;

const createRoleService = async () => {
  const { default: roleService } = await import('../services/roleService');
  await roleService.init();
  roleServiceInstance = roleService;
  return roleService;
};

// Loaded on first use, which AuthContext makes once a provisioned profile is
// signed in (initializeRoleService): the roles collections are readable only by
// provisioned users, so loading at import (signed out, on /login and /signup)
// was refused with an uncaught FirebaseError, and that rejected load was then
// kept for the whole session. A failed load is not kept: the next call retries.
// Until a load succeeds, hasPermission answers from the hardcoded matrix.
export const getRoleService = async () => {
  if (!roleServicePromise) {
    roleServicePromise = createRoleService().catch((error) => {
      roleServicePromise = null;
      throw error;
    });
  }
  return roleServicePromise;
};

// Helper function to check if a user has a specific permission
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;

  // SUPERADMIN always has all permissions - absolute bypass
  if (userRole === 'superadmin') {
    return true;
  }

  // Manufacturer Staff: exactly the manufacturer's effective permissions (Firestore
  // overrides included), minus user management.
  if (userRole === ROLES.MANUFACTURER_STAFF) {
    if (USER_MANAGEMENT_PERMISSIONS.includes(permission)) return false;
    return hasPermission(ROLES.MANUFACTURER, permission);
  }

  // User management only for roles the server accepts as user managers. Checked
  // before the role service so a `true` in a roles/{id} document or a role-editor
  // toggle cannot offer pages the server refuses.
  if (USER_MANAGEMENT_PERMISSIONS.includes(permission) && !USER_MANAGER_ROLES.includes(userRole)) {
    return false;
  }

  // DYNAMIC ROLE SERVICE - PRIMARY CHECK
  if (roleServiceInstance) {
    try {
      const result = roleServiceInstance.hasPermission(userRole, permission);

      // If database says YES, grant permission
      if (result === true) {
        return true;
      }

      // ROOT-CONTROL: honor an EXPLICIT Firestore `false` from the
      // /admin/role-permissions editor. Previously an "off" toggle was silently
      // re-granted by the hardcoded floor below, so the editor could GRANT a
      // feature but never HIDE one. Now an explicitly-false permission is denied.
      // An ABSENT key still falls through to the hardcoded baseline, so a role is
      // never locked out of a permission it was simply never given a value for.
      if (typeof roleServiceInstance.getExplicitFirestorePermission === 'function') {
        const explicit = roleServiceInstance.getExplicitFirestorePermission(userRole, permission);
        if (explicit === false) {
          return false;
        }
      }

      // If database does not define it, check hardcoded as fallback
      // This ensures we don't break existing functionality when DB is incomplete
    } catch (error) {
      console.error('Error using role service:', error);
      // Fall through to hardcoded fallback
    }
  }

  // FALLBACK/MERGE with hardcoded permissions
  // This ensures core permissions work even if DB is incomplete
  const roleKey = Object.entries(ROLES).find(([_, value]) => value === userRole)?.[1];
  if (roleKey && ROLE_PERMISSIONS[roleKey]) {
    const hardcodedPermissions = ROLE_PERMISSIONS[roleKey];
    if (hardcodedPermissions[permission] === true) {
      return true;
    }
  }
  
  // Additional hardcoded fallbacks for common roles
  if (userRole === 'admin') {
    const adminPermissions = ROLE_PERMISSIONS[ROLES.ADMIN];
    return adminPermissions && adminPermissions[permission] === true;
  }
  
  if (userRole === 'user') {
    const userPermissions = ROLE_PERMISSIONS[ROLES.USER];
    return userPermissions && userPermissions[permission] === true;
  }
  
  if (userRole === 'manufacturer') {
    const manufacturerPermissions = ROLE_PERMISSIONS[ROLES.MANUFACTURER];
    return manufacturerPermissions && manufacturerPermissions[permission] === true;
  }

  if (userRole === 'customer') {
    const customerPermissions = ROLE_PERMISSIONS[ROLES.CUSTOMER];
    return customerPermissions && customerPermissions[permission] === true;
  }

  return false;
};

// Helper function to check if user has any of the specified permissions (DATABASE ONLY)
export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  
  // Use the hasPermission function which now only uses database
  return permissions.some(permission => hasPermission(userRole, permission));
};

// Helper function to check if user has all of the specified permissions (DATABASE ONLY)
export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  
  // Use the hasPermission function which now only uses database
  return permissions.every(permission => hasPermission(userRole, permission));
};

// Initialize role service (call this during app startup)
export const initializeRoleService = async () => {
  try {
    const service = await getRoleService();
    console.log('✅ Role service initialized successfully');
    return service;
  } catch (error) {
    console.warn('⚠️ Failed to initialize role service, using hardcoded fallback:', error);
    return null;
  }
};

// Helper function to get all permissions for a role
export const getRolePermissions = (role) => {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return [];
  
  // If super admin, return all permissions
  if (permissions['*'] === true) {
    return Object.values(PERMISSIONS);
  }
  
  // Return only granted permissions
  return Object.entries(permissions)
    .filter(([_, granted]) => granted === true)
    .map(([permission]) => permission);
};

// Route access configuration
export const ROUTE_PERMISSIONS = {
  // Public routes - no permissions needed
  '/': null,
  '/login': null,
  '/signup': null,
  '/signup/verify': null,
  '/signup/finish': null,
  '/set-password': null,
  '/pricing': null,
  '/terms': null,
  '/privacy': null,
  
  // Protected routes
  '/dashboard': [PERMISSIONS.VIEW_DASHBOARD],
  '/shipments': [PERMISSIONS.VIEW_SHIPMENTS],
  '/create-shipment': [PERMISSIONS.CREATE_SHIPMENTS],
  '/tracking': [PERMISSIONS.VIEW_TRACKING],
  '/customers': [PERMISSIONS.VIEW_CUSTOMERS],
  '/reports': [PERMISSIONS.VIEW_REPORTS],
  '/billing': [PERMISSIONS.VIEW_BILLING],
  '/profile': [PERMISSIONS.VIEW_PROFILE],
  '/carriers': [PERMISSIONS.USE_QUICKSHIP], // QuickShip carrier management (company-scoped); /admin/carriers is the global integrations admin
  '/notifications': [PERMISSIONS.VIEW_NOTIFICATIONS],
  
  // Admin routes
  '/admin': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/dashboard': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  
  // Company Management
  '/admin/companies': [PERMISSIONS.VIEW_COMPANIES, PERMISSIONS.VIEW_ALL_COMPANIES],
  '/admin/companies/new': [PERMISSIONS.CREATE_COMPANIES],
  '/admin/companies/:id': [PERMISSIONS.VIEW_COMPANIES, PERMISSIONS.VIEW_ALL_COMPANIES],
  '/admin/companies/:id/edit': [PERMISSIONS.EDIT_COMPANIES],
  
  // Customer Management
  '/admin/customers': [PERMISSIONS.VIEW_CUSTOMERS, PERMISSIONS.VIEW_ALL_CUSTOMERS],
  '/admin/customers/new': [PERMISSIONS.CREATE_CUSTOMERS],
  '/admin/customers/:id': [PERMISSIONS.VIEW_CUSTOMERS, PERMISSIONS.VIEW_ALL_CUSTOMERS],
  '/admin/customers/:id/edit': [PERMISSIONS.EDIT_CUSTOMERS],
  
  // Broker Management
  '/admin/brokers': [PERMISSIONS.MANAGE_BROKERS],
  '/admin/brokers/new': [PERMISSIONS.MANAGE_BROKERS],
  '/admin/brokers/:id': [PERMISSIONS.MANAGE_BROKERS],
  '/admin/brokers/:id/edit': [PERMISSIONS.MANAGE_BROKERS],
  
  // Organization Management
  '/admin/organizations': [PERMISSIONS.VIEW_ORGANIZATIONS],
  '/admin/organizations/new': [PERMISSIONS.CREATE_ORGANIZATIONS],
  '/admin/organizations/:id': [PERMISSIONS.VIEW_ORGANIZATIONS],
  '/admin/organizations/:id/edit': [PERMISSIONS.EDIT_ORGANIZATIONS],
  
  // User Management
  '/admin/users': [PERMISSIONS.VIEW_USERS],
  '/admin/users/new': [PERMISSIONS.CREATE_USERS, PERMISSIONS.INVITE_USERS],
  '/admin/users/:id': [PERMISSIONS.VIEW_USERS],
  '/admin/users/:id/edit': [PERMISSIONS.EDIT_USERS],
  
  // Shipment Management
  // VIEW_SHIPMENTS allows customer role access (filtered in component)
  // VIEW_ALL_SHIPMENTS allows admin access to all shipments
  '/admin/shipments': [PERMISSIONS.VIEW_SHIPMENTS, PERMISSIONS.VIEW_ALL_SHIPMENTS],
  '/admin/shipment/:id': [PERMISSIONS.VIEW_SHIPMENTS, PERMISSIONS.VIEW_ALL_SHIPMENTS],
  // Booking-reconcile queue: a permission no Firestore role doc sets, so only the
  // hardcoded ADMIN grant (and superadmin's '*') opens it.
  '/admin/booking-reconcile': [PERMISSIONS.RESOLVE_BOOKING_RECONCILE],

  // Billing Management
  // VIEW_BILLING allows customer role access (filtered in component)
  // VIEW_ALL_INVOICES allows admin access to all invoices
  // /admin/billing is the BACK-OFFICE dashboard: its reads are unscoped
  // (whole-collection companies / shipments / customers / ediResults), so the
  // server refuses them for anyone who is not a billing role. VIEW_BILLING is
  // held by Company Admin, Company Staff and customer too, which let those roles
  // open a page that could only ever render an error. Gate on VIEW_ALL_INVOICES,
  // which matches firestore.rules isBillingRole() exactly
  // (superadmin|admin|accounting). Company-scoped invoice viewing has
  // its own properly-scoped surface (src/components/Billing/Invoices.jsx).
  '/admin/billing': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/overview': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/orphans': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/invoiced-no-carrier-bill': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/ready-to-invoice': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/ready-to-deliver': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/invoice-exceptions': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/charges': [PERMISSIONS.VIEW_BILLING, PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/invoice/new': [PERMISSIONS.CREATE_INVOICES],
  '/admin/billing/invoice/:id': [PERMISSIONS.VIEW_BILLING, PERMISSIONS.EDIT_INVOICES],
  '/admin/billing/ap-processing': [PERMISSIONS.VIEW_BILLING, PERMISSIONS.MANAGE_AP_PROCESSING],
  '/admin/billing/generate': [PERMISSIONS.GENERATE_INVOICES],
  '/admin/billing/business': [PERMISSIONS.VIEW_BILLING, PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/billing/payments': [PERMISSIONS.VIEW_PAYMENTS, PERMISSIONS.VIEW_BILLING],
  '/admin/billing/commissions': [PERMISSIONS.VIEW_COMMISSIONS, PERMISSIONS.VIEW_BILLING],
  '/admin/billing/payment-methods': [PERMISSIONS.VIEW_BILLING], // Customer portal can access payment methods
  '/admin/billing/payment-terms': [PERMISSIONS.MANAGE_PAYMENT_TERMS],
  '/admin/reseller-invoices': [PERMISSIONS.VIEW_ALL_INVOICES],
  '/admin/reseller-invoices/:tab': [PERMISSIONS.VIEW_ALL_INVOICES],
  
  // System Management
  '/admin/role-permissions': [PERMISSIONS.MANAGE_ROLES], // Role permissions management
  '/admin/settings': [PERMISSIONS.MANAGE_SYSTEM_SETTINGS],
  '/admin/configuration': [PERMISSIONS.MANAGE_SYSTEM_SETTINGS],
  // Soluship 2.0 bridge portal — superadmin only (no role holds VIEW_DEVELOPER_PORTAL_V2)
  '/admin/developer-portal-v2': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],
  '/admin/developer-portal-v2/mcp': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],
  '/admin/developer-portal-v2/playground': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],
  '/admin/developer-portal-v2/playground/:endpointId': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],
  '/admin/developer-portal-v2/data-sync': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],
  '/admin/developer-portal-v2/data-sync/:connectionId': [PERMISSIONS.VIEW_DEVELOPER_PORTAL_V2],

  // Developer tools. Company users manage their own API keys, webhooks and integrations,
  // so these stay open to every role with the admin dashboard. The two sandboxes
  // call carrier and platform APIs directly and are platform-admin only.
  '/admin/api-keys': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/api-keys/:keyId': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/api-docs': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/api-docs/mcp': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/developer-portal': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/webhooks': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/integrations': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
  '/admin/api-sandbox': [PERMISSIONS.MANAGE_SYSTEM_SETTINGS],
  '/admin/carrier-sandbox': [PERMISSIONS.MANAGE_SYSTEM_SETTINGS],

  // Atlas 2.0 voice AI agent — superadmin only (no role holds VIEW_ATLAS_AGENT)
  '/admin/atlas-2-0': [PERMISSIONS.VIEW_ATLAS_AGENT],

  // Carrier Management
  '/admin/carrier-keys': [PERMISSIONS.MANAGE_CARRIER_KEYS],
  '/admin/carriers': [PERMISSIONS.VIEW_CARRIERS, PERMISSIONS.MANAGE_CARRIER_KEYS],
  '/admin/carriers/new': [PERMISSIONS.CREATE_CARRIERS],
  '/admin/carriers/:carrierId': [PERMISSIONS.VIEW_CARRIERS],
  '/admin/carriers/:carrierId/edit': [PERMISSIONS.EDIT_CARRIERS],
  
  // Pricing & Markups
  '/admin/markups': [PERMISSIONS.MANAGE_MARKUPS],
  '/admin/markups/:tab': [PERMISSIONS.MANAGE_MARKUPS],
  '/admin/edi-mapping': [PERMISSIONS.MANAGE_EDI_MAPPING],
  
  // Address Management
  '/admin/addresses': [PERMISSIONS.VIEW_ADDRESSES, PERMISSIONS.VIEW_ALL_ADDRESSES],
  '/admin/carrier-accounts': [PERMISSIONS.EDIT_COMPANIES],
  
  // Other Admin Features
  '/admin/followups': [PERMISSIONS.VIEW_FOLLOWUPS],
  '/admin/profile': [PERMISSIONS.VIEW_PROFILE],
  '/admin/notifications': [PERMISSIONS.VIEW_ADMIN_DASHBOARD],
};

// Helper function to match a route against a pattern (handles :id style params)
const matchRoutePattern = (route, pattern) => {
  // Exact match
  if (route === pattern) return true;
  
  // Convert pattern to regex (replace :param with a generic match)
  const regexPattern = pattern
    .replace(/:[^/]+/g, '[^/]+')  // Replace :id, :param with regex
    .replace(/\//g, '\\/');       // Escape slashes
  
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(route);
};

// Helper function to find matching route permissions. Returns undefined when the
// route is not mapped at all (null means mapped as public).
const findRoutePermissions = (route) => {
  // First try exact match
  if (Object.prototype.hasOwnProperty.call(ROUTE_PERMISSIONS, route)) {
    return ROUTE_PERMISSIONS[route];
  }

  // Then try pattern matching
  for (const pattern of Object.keys(ROUTE_PERMISSIONS)) {
    if (pattern.includes(':') && matchRoutePattern(route, pattern)) {
      return ROUTE_PERMISSIONS[pattern];
    }
  }

  return undefined;
};

// React Router matches paths case-insensitively and ignores a trailing slash, so
// /admin/Users/ renders the same page as /admin/users. Normalise before the lookup.
const normalizeRoute = (route) => {
  const path = String(route || '').toLowerCase().replace(/\/{2,}/g, '/');
  return path.length > 1 ? path.replace(/\/$/, '') : path;
};

const isAdminPath = (path) => path === '/admin' || path.startsWith('/admin/');

// Helper function to check if user can access a route.
// `allows` (optional) narrows the role's grants: a permission it refuses does not open the
// route. AdminRoute passes the self-serve overlay (src/utils/selfServe.js selfServeAllows),
// so a route open to a role only through a surface hidden from self-serve members is closed
// to them. Without it the answer is the role's alone.
export const canAccessRoute = (userRole, route, { allows } = {}) => {
  // BULLETPROOF: SUPERADMIN ALWAYS HAS ACCESS - NO EXCEPTIONS
  if (userRole === 'superadmin') {
    return true;
  }

  const path = normalizeRoute(route);
  let requiredPermissions = findRoutePermissions(path);

  // No entry: inherit the nearest mapped parent (/admin/shipments/ICAL-123 ->
  // /admin/shipments). The walk stops below /admin itself, so an admin page with
  // no mapping anywhere is denied instead of inheriting the dashboard's open gate.
  let parent = path;
  while (requiredPermissions === undefined && isAdminPath(parent)) {
    parent = parent.slice(0, parent.lastIndexOf('/'));
    if (parent === '/admin') break;
    requiredPermissions = findRoutePermissions(parent);
  }

  // Unmapped admin pages fail closed; other unmapped routes stay open as before.
  if (requiredPermissions === undefined) return !isAdminPath(path);

  // Public routes (no permissions required)
  if (!requiredPermissions) return true;

  // Check if user has any of the required permissions
  if (typeof allows === 'function') {
    return requiredPermissions.some((permission) => allows(permission) && hasPermission(userRole, permission));
  }
  return hasAnyPermission(userRole, requiredPermissions);
};

// Navigation menu configuration based on permissions
export const getNavigationMenu = (userRole) => {
  const menu = [];
  
  // Dashboard
  if (hasPermission(userRole, PERMISSIONS.VIEW_DASHBOARD)) {
    menu.push({
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'Dashboard',
    });
  }
  
  // Shipments
  if (hasPermission(userRole, PERMISSIONS.VIEW_SHIPMENTS)) {
    menu.push({
      label: 'Shipments',
      path: '/shipments',
      icon: 'LocalShipping',
    });
  }
  
  // Create Shipment
  if (hasPermission(userRole, PERMISSIONS.CREATE_SHIPMENTS)) {
    menu.push({
      label: 'Create Shipment',
      path: '/create-shipment',
      icon: 'Add',
    });
  }
  
  // Customers
  if (hasPermission(userRole, PERMISSIONS.VIEW_CUSTOMERS)) {
    menu.push({
      label: 'Customers',
      path: '/customers',
      icon: 'People',
    });
  }
  
  // Tracking
  if (hasPermission(userRole, PERMISSIONS.VIEW_TRACKING)) {
    menu.push({
      label: 'Tracking',
      path: '/tracking',
      icon: 'LocationOn',
    });
  }
  
  // Reports
  if (hasPermission(userRole, PERMISSIONS.VIEW_REPORTS)) {
    menu.push({
      label: 'Reports',
      path: '/reports',
      icon: 'Assessment',
    });
  }
  
  // Billing
  if (hasPermission(userRole, PERMISSIONS.VIEW_BILLING)) {
    menu.push({
      label: 'Billing',
      path: '/billing',
      icon: 'Receipt',
    });
  }
  
  // Carriers
  if (hasPermission(userRole, PERMISSIONS.VIEW_CARRIERS)) {
    menu.push({
      label: 'Carriers',
      path: '/carriers',
      icon: 'LocalShipping',
    });
  }
  
  // Admin Menu
  if (hasPermission(userRole, PERMISSIONS.VIEW_ADMIN_DASHBOARD)) {
    menu.push({
      label: 'Admin',
      path: '/admin',
      icon: 'AdminPanelSettings',
      submenu: getAdminSubmenu(userRole),
    });
  }
  
  return menu;
};

// Get admin submenu based on permissions
export const getAdminSubmenu = (userRole) => {
  const submenu = [];
  
  if (hasPermission(userRole, PERMISSIONS.VIEW_ADMIN_DASHBOARD)) {
    submenu.push({
      label: 'Dashboard',
      path: '/admin/dashboard',
      icon: 'Dashboard',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.VIEW_COMPANIES)) {
    submenu.push({
      label: 'Companies',
      path: '/admin/companies',
      icon: 'Business',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.VIEW_USERS)) {
    submenu.push({
      label: 'Users',
      path: '/admin/users',
      icon: 'People',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.VIEW_ORGANIZATIONS)) {
    submenu.push({
      label: 'Organizations',
      path: '/admin/organizations',
      icon: 'AccountTree',
    });
  }
  
  // Shipments - show if user has VIEW_SHIPMENTS OR VIEW_ALL_SHIPMENTS
  // Data is filtered in component based on user's company access
  if (hasPermission(userRole, PERMISSIONS.VIEW_ALL_SHIPMENTS) || 
      hasPermission(userRole, PERMISSIONS.VIEW_SHIPMENTS)) {
    submenu.push({
      label: 'Shipments',
      path: '/admin/shipments',
      icon: 'LocalShipping',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.VIEW_ALL_ADDRESSES)) {
    submenu.push({
      label: 'Addresses',
      path: '/admin/addresses',
      icon: 'ContactMail',
    });
  }
  
  // Billing - show if user has VIEW_BILLING OR VIEW_ALL_INVOICES
  // Data is filtered in component based on user's company access
  if (hasPermission(userRole, PERMISSIONS.VIEW_ALL_INVOICES) || 
      hasPermission(userRole, PERMISSIONS.VIEW_BILLING)) {
    submenu.push({
      label: 'Billing',
      path: '/admin/billing',
      icon: 'Receipt',
    });
  }
  
  // Customers - show for users with VIEW_CUSTOMERS permission
  if (hasPermission(userRole, PERMISSIONS.VIEW_CUSTOMERS)) {
    submenu.push({
      label: 'Customers',
      path: '/admin/customers',
      icon: 'People',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.MANAGE_CARRIERS)) {
    submenu.push({
      label: 'Carriers',
      path: '/admin/carriers',
      icon: 'LocalShipping',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.MANAGE_MARKUPS)) {
    submenu.push({
      label: 'Markups',
      path: '/admin/markups',
      icon: 'AttachMoney',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.MANAGE_ROLES)) {
    submenu.push({
      label: 'Permissions',
      path: '/admin/role-permissions',
      icon: 'Security',
    });
  }
  
  if (hasPermission(userRole, PERMISSIONS.MANAGE_SETTINGS)) {
    submenu.push({
      label: 'Settings',
      path: '/admin/settings',
      icon: 'Settings',
    });
  }
  
  return submenu;
};

// Feature flags based on permissions
export const FEATURE_FLAGS = {
  SHOW_QUICKSHIP: PERMISSIONS.USE_QUICKSHIP,
  SHOW_AI_AGENT: PERMISSIONS.USE_AI_AGENT,
  SHOW_ADVANCED_ROUTING: PERMISSIONS.USE_ADVANCED_ROUTING,
  SHOW_EXPORT_BUTTONS: PERMISSIONS.EXPORT_SHIPMENTS,
  SHOW_ADMIN_BUTTON: PERMISSIONS.VIEW_ADMIN_DASHBOARD,
  SHOW_COMPANY_SWITCHER: PERMISSIONS.VIEW_ALL_COMPANIES,
  SHOW_GLOBAL_SEARCH: PERMISSIONS.VIEW_ALL_SHIPMENTS,
  SHOW_BULK_ACTIONS: PERMISSIONS.EDIT_SHIPMENTS,
  SHOW_MANUAL_STATUS_UPDATE: PERMISSIONS.UPDATE_TRACKING,
};

// Helper to check feature availability
export const isFeatureEnabled = (userRole, featureFlag) => {
  const permission = FEATURE_FLAGS[featureFlag];
  return permission ? hasPermission(userRole, permission) : false;
};

// Shipment detail permission helpers
/**
 * @param {string} userRole
 * @param {{viewer?: boolean}|null} [selfServe]  useSelfServe(): for a member of a self-serve
 *   company (viewer) the detail page offers nothing the server refuses them (every such
 *   callable refuses a self-serve member; functions/src/selfServe/optIns.js lists the ones
 *   that admit them), and no carrier cost:
 *   - no carrier cost, markup or rate breakdown;
 *   - no archive or status override (a booked shipment's status changes only through the
 *     server, cancelShipment);
 *   - no pickup booking, change or cancel from the page (bookCarrierPickup and friends): a
 *     tier with pickups books them inside bookRateUniversal, at booking;
 *   - no follow-ups (the button, the dialog and the per-shipment load);
 *   - no document upload or delete (uploadShipmentDocument, deleteShipmentDocument);
 *   - no BOL, carrier confirmation or customs invoice regeneration, and no carrier
 *     confirmation (getShipmentDocuments withholds them from a member).
 */
export const getShipmentDetailPermissions = (userRole, selfServe = null) => {
  const permissions = getRoleShipmentDetailPermissions(userRole);
  if (!selfServe || !selfServe.viewer) return permissions;
  return {
    ...permissions,
    canArchiveShipment: false,
    canViewCosts: false,
    canViewShipmentFinancials: false,
    canViewFinancials: false,
    canManagePickups: false,
    canForceStatusRefresh: false,
    canViewFollowUps: false,
    canChangeDocuments: false,
    canRegenerateDocuments: false,
    canRegenerateCarrierConfirmation: false,
    canViewCarrierConfirmation: false,
  };
};

const getRoleShipmentDetailPermissions = (userRole) => {
  return {
    canEditShipment: hasPermission(userRole, PERMISSIONS.EDIT_SHIPMENTS),
    canViewDocuments: hasPermission(userRole, PERMISSIONS.VIEW_DOCUMENTS),
    canViewBOL: hasPermission(userRole, PERMISSIONS.VIEW_BOL),
    canViewCarrierConfirmation: hasPermission(userRole, PERMISSIONS.VIEW_CARRIER_CONFIRMATION),
    canViewFollowUps: hasPermission(userRole, PERMISSIONS.VIEW_FOLLOW_UPS),
    canArchiveShipment: hasPermission(userRole, PERMISSIONS.ARCHIVE_SHIPMENT),
    canCancelShipment: hasPermission(userRole, PERMISSIONS.CANCEL_SHIPMENT),
    canViewCosts: hasPermission(userRole, PERMISSIONS.VIEW_SHIPMENT_COSTS),
    // Keep both keys for compatibility. Components commonly check canViewShipmentFinancials
    canViewShipmentFinancials: hasPermission(userRole, PERMISSIONS.VIEW_SHIPMENT_FINANCIALS),
    canViewFinancials: hasPermission(userRole, PERMISSIONS.VIEW_SHIPMENT_FINANCIALS),
    canShowRateFilters: userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ADMIN
      ? true
      : hasPermission(userRole, PERMISSIONS.SHOW_RATE_FILTERS),
    // Mirrors the server gates (functions/src/shipment-management/shipmentAccess.js): booking,
    // changing or cancelling a pickup and generating/regenerating documents need a
    // shipment-write grant; regenerating a carrier confirmation also needs its own grant.
    canManagePickups: hasAnyPermission(userRole, [PERMISSIONS.CREATE_SHIPMENTS, PERMISSIONS.EDIT_SHIPMENTS]),
    canRegenerateDocuments: hasAnyPermission(userRole, [PERMISSIONS.CREATE_SHIPMENTS, PERMISSIONS.EDIT_SHIPMENTS]),
    canRegenerateCarrierConfirmation: hasAnyPermission(userRole, [PERMISSIONS.CREATE_SHIPMENTS, PERMISSIONS.EDIT_SHIPMENTS]) &&
      hasPermission(userRole, PERMISSIONS.GENERATE_CARRIER_CONFIRMATIONS),
    // The confirmed "re-check carrier and replace status" override (forceStatusRefresh mode
    // 'override'); the plain Refresh never overrides a manual status.
    canForceStatusRefresh: hasPermission(userRole, PERMISSIONS.MANUAL_STATUS_OVERRIDE),
    // Upload and delete in the Documents section. The section still applies its own role and
    // company checks (DocumentsSection canChangeDocuments); only the self-serve overlay turns
    // this off.
    canChangeDocuments: true,
  };
}; 